/**
 * @file Feature flags, resolved server side and fetched from the API.
 *
 * The previous implementation constructed an Azure AppConfigurationClient in the browser from
 * VUE_APP_AZURE_APP_CONFIG_CONNECTION_STRING. Vue CLI inlines every VUE_APP_* variable into the
 * bundle, so that connection string - including its HMAC secret - was shipped to every visitor.
 * The API now reads App Configuration using the App Service's managed identity, so there is no
 * credential on this side at all.
 *
 * Deliberately uses fetch rather than axios: the shared axios instance carries an interceptor that
 * logs the user out on a 401, and this runs during boot before an account may exist.
 */

import { apiConfig } from "@/msal/apiConfig";

const CACHE_KEY = "pst_feature_flags_cache";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const FLAGS_ROUTE = "/movement-assessment/api/feature-flags";

/**
 * Flags gate what renders during boot, so a request that hangs rather than fails would
 * leave callers awaiting a promise that never settles - LibraryPage spins forever on it.
 * Capped low enough to stay within a page load; the stale cache covers the timeout.
 *
 * The retry below shares this budget rather than adding to it: two attempts plus the
 * backoff come to ~6.4s, which is what the router guards that await these flags (see
 * the beforeEnter hooks in router/routes.js) block navigation for in the worst case.
 */
const REQUEST_TIMEOUT_MS = 3000;

/**
 * A single retry, because the failure it exists for is the API's cold start: the first
 * request is what wakes the App Service and is also what times out, so the second one
 * usually lands on a warm instance. A third attempt only adds latency to a real outage.
 */
const RETRY_DELAY_MS = 400;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * WebKit - Safari, and the WKWebView the Unity app embeds - rejects every in-flight fetch
 * with "TypeError: Load failed" once the page starts navigating away. The Unity entry
 * routes (/auth-redirect, /refresh-token, /handshake) boot this request like any other
 * page, then redirect to B2C or are closed by the app almost immediately, so the request
 * is routinely cut off mid-flight. That rejection says nothing about the API: retrying it
 * is pointless and reporting it is noise, so both are skipped while the page is leaving.
 *
 * beforeunload is the one that matters - WebKit cancels loads when the navigation starts,
 * before pagehide - and pagehide covers exits that skip it. pageshow clears the flag when
 * the page comes back from the back/forward cache.
 */
let pageIsLeaving = false;
if (typeof window !== "undefined") {
  const markLeaving = () => {
    pageIsLeaving = true;
  };
  window.addEventListener("beforeunload", markLeaving);
  window.addEventListener("pagehide", markLeaving);
  window.addEventListener("pageshow", () => {
    pageIsLeaving = false;
  });
}

/**
 * `navigator.onLine === false` is definitive - there is no interface to send on, so no
 * number of attempts will help. A true value is not evidence of the opposite (it only
 * says an interface exists), which is why it is never read as "online".
 *
 * @returns {boolean} true only when the browser is certain there is no connection
 */
function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Aborts, transport failures and 5xx are all transient, so a second attempt can
 * plausibly succeed. A 4xx says the request itself is wrong - a renamed route, a label
 * the API rejects - and would fail identically, so it is answered from the cache at once.
 *
 * @param {*} error
 * @returns {boolean}
 */
function isRetryable(error) {
  // Absent on aborts, transport failures and body-parse failures: no response arrived,
  // or not a complete one, and none of those rule out a second attempt working.
  return typeof error?.status === "number" ? error.status >= 500 : true;
}

/**
 * The App Configuration store holds one identical set of flags per environment, keyed by label:
 * "dev", "staging", and production as the unlabelled set.
 *
 * Sending the label keeps this build's environment independent of whichever API it is pointed at -
 * which is what lets a local dashboard run against a deployed API and still get dev flags.
 *
 * The distinction between an unset and an empty variable is meaningful to the API:
 *   unset -> omit the parameter, and the API uses its own ENV_FEATURE_FLAG_LABEL
 *   ""    -> send it, which the API reads as an explicit request for production flags
 *
 * @returns {string|null} the configured label, or null when the variable is unset
 */
function currentLabel() {
  const label = process.env.VUE_APP_FEATURE_FLAG_LABEL;
  return label === undefined || label === null ? null : label;
}

/**
 * @returns {string} the flags endpoint, carrying ?label= only when one is configured
 */
function flagsUrl() {
  const base = `${apiConfig.apiUrl}${FLAGS_ROUTE}`;
  const label = currentLabel();
  return label === null ? base : `${base}?label=${encodeURIComponent(label)}`;
}

/**
 * Picks the requested names out of a flag map, coercing to a strict boolean so a
 * consumer can never be handed a truthy string such as "false".
 *
 * @param {string[]} flagNames
 * @param {Record<string, unknown>} flags
 * @returns {Record<string, boolean>}
 */
function resolve(flagNames, flags) {
  return Object.fromEntries(flagNames.map((n) => [n, flags[n] === true]));
}

/**
 * Fetches the flag map once, aborting if the API does not answer in time. Abort,
 * transport and parse failures all surface as a rejection for the caller to fall back on.
 *
 * @returns {Promise<unknown>} the parsed response body, of unverified shape
 */
async function fetchFlagsOnce() {
  const controller = new AbortController();
  // Held open until the body is parsed, so a stalled response stream aborts too.
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(flagsUrl(), { signal: controller.signal });
    if (!response.ok) {
      const error = new Error(
        `Feature flag request failed: ${response.status}`,
      );
      // Carried so isRetryable can separate a transient 5xx from a bad request.
      error.status = response.status;
      throw error;
    }
    return await response.json();
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetches the flag map, retrying once when the caller has nothing to fall back on.
 *
 * Every flag now comes from this one request, so unlike the per-flag fetches this
 * replaced there is no partial success: one blip resolves the whole set to false, and
 * silently, for as long as the page lives. That is what the retry is buying.
 *
 * @param {boolean} allowRetry
 * @returns {Promise<unknown>} the parsed response body, of unverified shape
 */
async function fetchFlags(allowRetry) {
  try {
    return await fetchFlagsOnce();
  } catch (error) {
    if (!allowRetry || !isRetryable(error) || isOffline() || pageIsLeaving) {
      throw error;
    }

    console.warn(
      `Feature flag fetch failed, retrying in ${RETRY_DELAY_MS}ms:`,
      error,
    );
    await delay(RETRY_DELAY_MS);
    return fetchFlagsOnce();
  }
}

/**
 * @returns {{timestamp: number, label: string|null, flags: Record<string, unknown>}|null}
 *   the cached entry, or null if it is absent, unreadable, or was written for another label
 */
function readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const cache = JSON.parse(raw);
    // A cache written by a build targeting a different environment is not ours to reuse.
    return cache.label === currentLabel() ? cache : null;
  } catch (e) {
    console.warn("Failed to read feature flags cache from localStorage:", e);
    return null;
  }
}

/**
 * @param {Record<string, unknown>} flags the full flag map to cache, not just the requested names
 */
function writeCache(flags) {
  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({ timestamp: Date.now(), label: currentLabel(), flags }),
    );
  } catch (e) {
    console.warn("Failed to write feature flags cache to localStorage:", e);
  }
}

export default {
  /**
   * @param {string[]} flagNames
   * @returns {Promise<Record<string, boolean>>} requested flags, defaulting to false
   */
  async getFeatureFlags(flagNames) {
    const cache = readCache();
    const cachedFlags = (cache && cache.flags) || null;
    const cacheIsFresh =
      cachedFlags && Date.now() - cache.timestamp < CACHE_TTL_MS;
    // Only a cache holding every requested name is a real fallback; one missing a name
    // answers it with the same false a total failure would.
    const canFallBack =
      cachedFlags !== null && flagNames.every((name) => name in cachedFlags);

    if (cacheIsFresh && canFallBack) {
      return resolve(flagNames, cachedFlags);
    }

    try {
      // The cache and the retry are alternative safety nets, so only one is paid for:
      // with a usable cache a second attempt would just add latency to a navigation
      // that is already blocked on this promise.
      const body = await fetchFlags(!canFallBack);
      if (!body || typeof body !== "object" || Array.isArray(body)) {
        throw new Error("Feature flag response was not an object");
      }

      const resolved = resolve(flagNames, body);
      // Cache the resolved values on top of the response so a name the API does not
      // know about is remembered as false, rather than missing from the cache and
      // therefore re-fetched on every single call.
      writeCache({ ...body, ...resolved });
      return resolved;
    } catch (error) {
      if (pageIsLeaving) {
        console.debug("Feature flag fetch cut off by page navigation:", error);
      } else {
        console.error("Unexpected error fetching feature flags:", error);
      }
      // Fall back to the last known values so a transient API failure does not
      // flip every feature off mid-session.
      return resolve(flagNames, cachedFlags || {});
    }
  },

  /**
   * @param {string} flagName
   * @returns {Promise<boolean>}
   */
  async getFeatureFlag(flagName) {
    const flags = await this.getFeatureFlags([flagName]);
    return flags[flagName];
  },
};
